/**
 * Experience Mapper Utility
 *
 * Maps API Experience objects to ExtendedExperienceCard format
 * Used for converting API responses to component-compatible format
 */

import { Experience } from '@/services/experienceService';
import { ExtendedExperienceCard } from '@/constants/experiencesData';
import { getExperienceImage } from '@/services/experienceService';

/**
 * Map API Experience to ExtendedExperienceCard
 */
export function mapExperienceToCard(
  experience: Experience
): ExtendedExperienceCard {
  // Get display image using the service utility
  const image = getExperienceImage(experience);

  // Get all available images for carousel
  const getAllImages = (): string[] => {
    const images: string[] = [];

    // First, get images from media array
    if (experience.media && Array.isArray(experience.media)) {
      const mediaImages = experience.media
        .filter(item => item.format === 'image' && item.isActive)
        .map(item => item.url);
      images.push(...mediaImages);
    }

    // Then add images from imageUrl array
    if (experience.imageUrl && Array.isArray(experience.imageUrl)) {
      images.push(...experience.imageUrl);
    }

    // Remove duplicates and ensure we have at least the primary image
    const uniqueImages = Array.from(new Set(images));
    if (uniqueImages.length === 0 && image) {
      uniqueImages.push(image);
    }

    return uniqueImages;
  };

  const allImages = getAllImages();

  // Get location string
  const getLocation = (): string => {
    if (experience.location) {
      const locationStr =
        `${experience.location.city || experience.location.name || ''}${experience.location.state ? `, ${experience.location.state}` : ''}`.trim();
      return locationStr || 'Location TBD';
    }
    return 'Location TBD';
  };

  const location = getLocation();

  // Get display title (prefer shortTitle, fallback to title or name)
  // Ensure we always have a title - use title if shortTitle is empty/null
  // Helper to check if a string is non-empty
  const isNonEmpty = (str: string | null | undefined): boolean => {
    return (
      str !== null &&
      str !== undefined &&
      typeof str === 'string' &&
      str.trim().length > 0
    );
  };

  // Get title with proper fallback chain
  // Priority: shortTitle (if non-empty) > title > name
  const title = isNonEmpty(experience.shortTitle)
    ? experience.shortTitle!.trim()
    : isNonEmpty(experience.title)
      ? experience.title!.trim()
      : isNonEmpty(experience.name)
        ? experience.name.trim()
        : 'Experience';

  // Map type from API to component type
  // Signature Journeys are packages with curated: true
  // Handle both 'type' and 'experienceType' fields from API
  const getApiType = (): string => {
    // Check for experienceType first (from chat API), then type (from experiences API)
    const experienceRecord = experience as unknown as Record<string, unknown>;
    const experienceType =
      typeof experienceRecord.experienceType === 'string'
        ? experienceRecord.experienceType
        : '';
    const apiType = experienceType || experience.type || '';
    return apiType.toLowerCase();
  };

  const mapType = (): ExtendedExperienceCard['type'] => {
    const apiType = getApiType();

    // If it's a package and curated, it's a Signature Journey
    if (apiType === 'package' && experience.curated === true) {
      return 'signature';
    }

    switch (apiType) {
      case 'signature':
        return 'signature';
      case 'package':
        return 'package';
      case 'activity':
      case 'guidedactivity':
        return 'activities';
      case 'stay_experience':
      case 'stayexperience':
      case 'stays':
        return 'stays';
      case 'riders_corner':
      case 'riderscorner':
        return 'riders-corner';
      default:
        return 'signature'; // Default fallback
    }
  };

  // Get pricing information - handle both simple price and pricing object
  // Priority: pricing object (with group pricing logic) > simple price field
  const getDisplayPrice = (): {
    price: string | undefined;
    currencySymbol: string;
  } => {
    // Check pricing object first (for stay experiences and any other types that have it)
    if (experience.pricing) {
      const pricing = experience.pricing;
      const currencySymbol = pricing.currencySymbol || '₹';

      // Check for display group pricing (isDisplayPrice: true)
      // This is the preferred pricing to show
      const displayGroup = pricing.groupPricing?.find(
        g => g.isDisplayPrice === true
      );

      let priceValue: number | undefined;

      if (displayGroup && displayGroup.adultPrice) {
        // Group tier adultPrice is per person per night
        priceValue = displayGroup.adultPrice;
      } else if (pricing.pricePerPerson) {
        // Use pricePerPerson (for stay experiences, this is per person per night)
        priceValue = pricing.pricePerPerson;
      }

      if (priceValue !== undefined) {
        return {
          price: priceValue.toFixed(2),
          currencySymbol,
        };
      }
    }

    // Fallback to simple price field (for other experience types)
    if (experience.price !== null && experience.price !== undefined) {
      return {
        price: experience.price.toFixed(2),
        currencySymbol: experience.currencySymbol || '₹',
      };
    }

    return {
      price: undefined,
      currencySymbol: experience.currencySymbol || '₹',
    };
  };

  const { price, currencySymbol } = getDisplayPrice();

  // Format duration
  const duration = experience.duration
    ? experience.duration.days
      ? `${experience.duration.days} ${experience.duration.days === 1 ? 'Day' : 'Days'}`
      : experience.duration.hours
        ? `${experience.duration.hours} ${experience.duration.hours === 1 ? 'Hour' : 'Hours'}`
        : undefined
    : undefined;

  // Get description with fallback chain
  // Check multiple possible description fields from API
  const experienceRecord = experience as unknown as Record<string, unknown>;
  const getDescription = (): string | undefined => {
    // Priority: description > shortDescription > overview > category.description
    if (experience.description && experience.description.trim()) {
      return experience.description.trim();
    }
    if (
      experienceRecord.shortDescription &&
      typeof experienceRecord.shortDescription === 'string' &&
      experienceRecord.shortDescription.trim()
    ) {
      return experienceRecord.shortDescription.trim();
    }
    if (
      experienceRecord.overview &&
      typeof experienceRecord.overview === 'string' &&
      experienceRecord.overview.trim()
    ) {
      return experienceRecord.overview.trim();
    }
    if (
      experience.category?.description &&
      experience.category.description.trim()
    ) {
      return experience.category.description.trim();
    }
    return undefined;
  };

  // Get currency code - prefer currency field from experience
  const getCurrency = (): string => {
    const experienceRecord = experience as unknown as Record<string, unknown>;

    // Prefer the currency field (e.g., "USD", "INR") as it's more reliable
    if (
      experienceRecord.currency &&
      typeof experienceRecord.currency === 'string'
    ) {
      return experienceRecord.currency.toUpperCase();
    }
    // Check in pricing object
    if (experience.pricing && typeof experience.pricing === 'object') {
      const pricing = experience.pricing as Record<string, unknown>;
      if (pricing.currency && typeof pricing.currency === 'string') {
        return pricing.currency.toUpperCase();
      }
    }
    // Fallback to currencySymbol inference
    if (currencySymbol === '₹' || currencySymbol === 'INR') return 'INR';
    if (currencySymbol === '$' || currencySymbol === 'USD') return 'USD';
    return 'USD'; // Default
  };

  // Get conversion rate from experience
  const getConversionRate = (): number | undefined => {
    const experienceRecord = experience as unknown as Record<string, unknown>;

    // Check for targetCurrencyRate
    if (
      experienceRecord.targetCurrencyRate !== null &&
      experienceRecord.targetCurrencyRate !== undefined
    ) {
      const rate =
        typeof experienceRecord.targetCurrencyRate === 'number'
          ? experienceRecord.targetCurrencyRate
          : parseFloat(String(experienceRecord.targetCurrencyRate));
      if (!isNaN(rate) && rate > 0) return rate;
    }

    // Check for conversionRate
    if (
      experienceRecord.conversionRate !== null &&
      experienceRecord.conversionRate !== undefined
    ) {
      const rate =
        typeof experienceRecord.conversionRate === 'number'
          ? experienceRecord.conversionRate
          : parseFloat(String(experienceRecord.conversionRate));
      if (!isNaN(rate) && rate > 0) return rate;
    }

    return undefined;
  };

  // Get tags from categories - extract all categories (main category + additional categories)
  const getTags = (): string[] => {
    const tags: string[] = [];
    const experienceRecord = experience as unknown as Record<string, unknown>;

    // Helper function to extract category name
    const extractCategoryName = (cat: unknown): string | null => {
      if (!cat) return null;
      if (typeof cat === 'string') {
        const trimmed = cat.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof cat === 'object' && cat !== null && !Array.isArray(cat)) {
        const catObj = cat as Record<string, unknown>;
        // Check multiple possible fields: title, name, description
        const title = catObj.title;
        const name = catObj.name;
        const description = catObj.description;
        const result =
          (typeof title === 'string' ? title.trim() : '') ||
          (typeof name === 'string' ? name.trim() : '') ||
          (typeof description === 'string' ? description.trim() : '');
        return result.length > 0 ? result : null;
      }
      return null;
    };

    // Add main category
    if (experience.category) {
      const catName = extractCategoryName(experience.category);
      if (catName && !tags.includes(catName)) {
        tags.push(catName);
      }
    }

    // Add additional categories from categories array (if API returns it)
    if (
      experienceRecord.categories &&
      Array.isArray(experienceRecord.categories) &&
      experienceRecord.categories.length > 0
    ) {
      experienceRecord.categories.forEach((cat: unknown) => {
        const catName = extractCategoryName(cat);
        if (catName && !tags.includes(catName)) {
          tags.push(catName);
        }
      });
    }

    return tags;
  };

  // Extract coordinates from various possible locations
  // Similar to mapChatExperienceToCard - check all possible coordinate sources
  let latitude: number | undefined;
  let longitude: number | undefined;

  // Check direct coordinates first
  // Reuse experienceRecord declared earlier in the function (line 191)
  if (
    experienceRecord.latitude !== undefined &&
    experienceRecord.longitude !== undefined
  ) {
    const lat = experienceRecord.latitude;
    const lng = experienceRecord.longitude;
    if (
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      !isNaN(lat) &&
      !isNaN(lng)
    ) {
      latitude = lat;
      longitude = lng;
    }
  }

  // Check location.coordinates.coordinates (GeoJSON format: [longitude, latitude])
  // The API provides coordinates in location.coordinates.coordinates
  // Note: The Experience interface doesn't include coordinates in location, but the API response does
  if (!latitude || !longitude) {
    // Access location from experienceRecord (bypasses TypeScript's strict typing)
    // Define type for location with GeoJSON coordinates
    type LocationWithCoordinates = {
      coordinates?: {
        type?: string;
        coordinates?: [number, number];
      };
    };
    const locationAny = experienceRecord.location as
      | LocationWithCoordinates
      | null
      | undefined;
    const locationIdAny = experienceRecord.locationId as
      | LocationWithCoordinates
      | undefined;

    // Try location.coordinates.coordinates first (most common in API response)
    if (locationAny?.coordinates?.coordinates) {
      const coords = locationAny.coordinates.coordinates;
      if (
        Array.isArray(coords) &&
        coords.length >= 2 &&
        typeof coords[0] === 'number' &&
        typeof coords[1] === 'number' &&
        !isNaN(coords[0]) &&
        !isNaN(coords[1])
      ) {
        // GeoJSON format: [longitude, latitude]
        longitude = coords[0];
        latitude = coords[1];
      }
    }
    // Fallback to locationId.coordinates.coordinates
    else if (locationIdAny?.coordinates?.coordinates) {
      const coords = locationIdAny.coordinates.coordinates;
      if (
        Array.isArray(coords) &&
        coords.length >= 2 &&
        typeof coords[0] === 'number' &&
        typeof coords[1] === 'number' &&
        !isNaN(coords[0]) &&
        !isNaN(coords[1])
      ) {
        // GeoJSON format: [longitude, latitude]
        longitude = coords[0];
        latitude = coords[1];
      }
    }
  }

  return {
    id: experience._id || experience.id || '',
    title,
    image,
    location,
    type: mapType(),
    slug: experience.slug,
    rating: experience.averageRating || 0,
    ratingCount: experience.reviewCount || 0,
    description: getDescription(),
    price,
    currencySymbol, // Currency from pricing object or experience
    currency: getCurrency(),
    backendConversionRate: getConversionRate(),
    duration,
    attractionsCount:
      typeof experience.attractionCount === 'number'
        ? experience.attractionCount
        : typeof experience.highlightsCount === 'number'
          ? experience.highlightsCount
          : experience.attractions?.length || 0,
    days: experience.duration?.days,
    nights: experience.duration?.nights,
    locationsCount: experience.location ? 1 : 0,
    tags: getTags(),
    images: allImages, // All images for carousel
    // Include coordinates if available
    latitude,
    longitude,
  };
}

/**
 * Map array of API Experiences to ExtendedExperienceCard array
 */
export function mapExperiencesToCards(
  experiences: Experience[]
): ExtendedExperienceCard[] {
  return experiences.map(mapExperienceToCard);
}

/**
 * Map Package/Activity to ExtendedExperienceCard
 * Used for related packages/activities on detail pages
 */
export function mapPackageToCard(
  pkg: Record<string, unknown>,
  type: 'package' | 'activities' = 'package'
): ExtendedExperienceCard {
  // Get primary image
  const image =
    pkg.imageUrl && Array.isArray(pkg.imageUrl) && pkg.imageUrl.length > 0
      ? pkg.imageUrl[0]
      : '/assets/images/No-Image.jpg';

  // Get all images for carousel
  const images =
    pkg.imageUrl && Array.isArray(pkg.imageUrl) && pkg.imageUrl.length > 0
      ? pkg.imageUrl
      : [image];

  // Get location string - check mainLocation first (API field), then location (backward compatibility)
  const getLocation = (): string => {
    // First check for mainLocation (preferred API field)
    if (pkg.mainLocation) {
      if (typeof pkg.mainLocation === 'object') {
        const loc = pkg.mainLocation as Record<string, unknown>;
        const locationStr =
          `${loc.city || loc.name || ''}${loc.state ? `, ${loc.state}` : ''}`.trim();
        return locationStr || 'Location TBD';
      }
    }

    // Fallback to location field (backward compatibility)
    if (pkg.location) {
      if (typeof pkg.location === 'string') {
        return pkg.location.trim() || 'Location TBD';
      }
      if (typeof pkg.location === 'object') {
        const loc = pkg.location as Record<string, unknown>;
        const locationStr =
          `${loc.city || loc.name || ''}${loc.state ? `, ${loc.state}` : ''}`.trim();
        return locationStr || 'Location TBD';
      }
    }

    return 'Location TBD';
  };

  // Get title
  const title =
    (typeof pkg.shortTitle === 'string' ? pkg.shortTitle.trim() : '') ||
    (typeof pkg.title === 'string' ? pkg.title.trim() : '') ||
    (typeof pkg.name === 'string' ? pkg.name.trim() : '') ||
    'Experience';

  // Get tags from categories
  const getTags = (): string[] => {
    const tags: string[] = [];

    // Helper function to extract category name
    const extractCategoryName = (cat: unknown): string | null => {
      if (!cat) return null;
      if (typeof cat === 'string') {
        const trimmed = cat.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof cat === 'object' && cat !== null && !Array.isArray(cat)) {
        const catObj = cat as Record<string, unknown>;
        // Check multiple possible fields: title, name, description
        const title = catObj.title;
        const name = catObj.name;
        const description = catObj.description;
        const result =
          (typeof title === 'string' ? title.trim() : '') ||
          (typeof name === 'string' ? name.trim() : '') ||
          (typeof description === 'string' ? description.trim() : '');
        return result.length > 0 ? result : null;
      }
      return null;
    };

    // Add main category
    if (pkg.category) {
      console.log(
        '[mapPackageToCard] Processing category field:',
        pkg.category
      );
      const catName = extractCategoryName(pkg.category);
      console.log('[mapPackageToCard] Extracted from category:', catName);
      if (catName && !tags.includes(catName)) {
        tags.push(catName);
      }
    }

    // Add additional categories from categories array
    if (
      pkg.categories &&
      Array.isArray(pkg.categories) &&
      pkg.categories.length > 0
    ) {
      console.log(
        '[mapPackageToCard] Processing categories array:',
        pkg.categories
      );
      pkg.categories.forEach((cat: unknown, index: number) => {
        const catName = extractCategoryName(cat);
        console.log(
          `[mapPackageToCard] Extracted from categories[${index}]:`,
          catName,
          'Raw:',
          cat
        );
        if (catName && !tags.includes(catName)) {
          tags.push(catName);
        } else if (catName) {
          console.log(
            `[mapPackageToCard] Skipping duplicate category:`,
            catName
          );
        }
      });
    }

    console.log('[mapPackageToCard] Final tags array:', tags);
    return tags;
  };

  // Get price
  const getPrice = (): string | undefined => {
    // Check for group pricing
    if (pkg.useGroupPricing && Array.isArray(pkg.groupBooking)) {
      const displayGroup = (pkg.groupBooking as Record<string, unknown>[]).find(
        (g: unknown) => (g as Record<string, unknown>).isDisplayPrice
      ) as Record<string, unknown> | undefined;
      if (
        displayGroup &&
        displayGroup.adultPrice &&
        displayGroup.minHeadCount
      ) {
        return (
          (displayGroup.adultPrice as number) *
          (displayGroup.minHeadCount as number)
        ).toFixed(2);
      }
    }

    // Fallback to regular price
    if (pkg.price !== null && pkg.price !== undefined) {
      return (pkg.price as number).toFixed(2);
    }

    return undefined;
  };

  // Get duration string
  const getDuration = (): string | undefined => {
    if (type === 'activities') {
      // For activities, use hours
      if (typeof pkg.hours === 'number') {
        return pkg.hours >= 60
          ? `${Math.floor(pkg.hours / 60)} hr${Math.floor(pkg.hours / 60) > 1 ? 's' : ''}${pkg.hours % 60 !== 0 ? ` ${pkg.hours % 60} min${pkg.hours % 60 > 1 ? 's' : ''}` : ''}`
          : `${pkg.hours} min${pkg.hours > 1 ? 's' : ''}`;
      }
    } else {
      // For packages, use days
      const duration = pkg.duration as Record<string, unknown> | undefined;
      if (duration?.days) {
        const days = duration.days as number;
        return `${days} ${days === 1 ? 'Day' : 'Days'}`;
      }
    }
    return undefined;
  };

  // Get conversion rate from API response
  const getConversionRate = (): number | undefined => {
    // Check for targetCurrencyRate (from API response)
    if (
      pkg.targetCurrencyRate !== null &&
      pkg.targetCurrencyRate !== undefined
    ) {
      const rate =
        typeof pkg.targetCurrencyRate === 'number'
          ? pkg.targetCurrencyRate
          : parseFloat(String(pkg.targetCurrencyRate));
      if (!isNaN(rate) && rate > 0) return rate;
    }

    // Check for conversionRate (alternative field)
    if (pkg.conversionRate !== null && pkg.conversionRate !== undefined) {
      const rate =
        typeof pkg.conversionRate === 'number'
          ? pkg.conversionRate
          : parseFloat(String(pkg.conversionRate));
      if (!isNaN(rate) && rate > 0) return rate;
    }

    return undefined;
  };

  // Get currency code - prefer currency field over currencySymbol
  const getCurrency = (): string => {
    // Prefer the currency field (e.g., "USD", "INR") as it's more reliable
    if (pkg.currency && typeof pkg.currency === 'string') {
      return pkg.currency.toUpperCase();
    }
    // Fallback to currencySymbol inference
    const symbol = (pkg.currencySymbol as string) || '₹';
    if (symbol === '₹' || symbol === 'INR') return 'INR';
    if (symbol === '$' || symbol === 'USD') return 'USD';
    return 'USD'; // Default
  };

  return {
    id: (pkg._id as string) || (pkg.id as string) || '',
    title,
    image,
    location: getLocation(),
    type: type === 'activities' ? 'activities' : 'package',
    slug: pkg.slug as string,
    rating: (pkg.averageRating as number) || 0,
    ratingCount: (pkg.reviewCount as number) || 0,
    description: pkg.description as string,
    price: getPrice(),
    currencySymbol: (pkg.currencySymbol as string) || '₹',
    currency: getCurrency(),
    backendConversionRate: getConversionRate(),
    duration: getDuration(),
    attractionsCount:
      type === 'activities'
        ? (pkg.activities as unknown[] | undefined)?.length || 0
        : typeof pkg.highlightsCount === 'number'
          ? pkg.highlightsCount
          : 0,
    days: (pkg.duration as Record<string, unknown> | undefined)?.days as
      | number
      | undefined,
    nights: (pkg.duration as Record<string, unknown> | undefined)?.nights as
      | number
      | undefined,
    locationsCount: (pkg.locationCount as number) || 0,
    tags: getTags(),
    images,
  };
}

/**
 * Map array of packages to ExtendedExperienceCard array
 */
export function mapPackagesToCards(
  packages: Record<string, unknown>[],
  type: 'package' | 'activities' = 'package'
): ExtendedExperienceCard[] {
  return packages.map(pkg => mapPackageToCard(pkg, type));
}

/** Stay media row counts as a gallery image unless explicitly deactivated (matches media API / booking snapshots). */
function isStayMediaImageRow(item: Record<string, unknown>): boolean {
  return (
    item.format === 'image' && item.isActive !== false && Boolean(item.url)
  );
}

/** Primary image URL for a stay experience (media, then imageUrl, then placeholder). */
export function getStayExperiencePrimaryImageUrl(
  stay: Record<string, unknown>
): string {
  if (stay.media && Array.isArray(stay.media)) {
    const imageMedia = stay.media.find((item: unknown) =>
      isStayMediaImageRow(item as Record<string, unknown>)
    ) as Record<string, unknown> | undefined;
    if (imageMedia?.url) return imageMedia.url as string;
  }
  if (
    stay.imageUrl &&
    Array.isArray(stay.imageUrl) &&
    stay.imageUrl.length > 0
  ) {
    const first = stay.imageUrl[0];
    if (typeof first === 'string' && first) return first;
  }
  return '/assets/images/No-Image.jpg';
}

/**
 * All image URLs for sliders/cards: active gallery media plus imageUrl (deduped).
 * Use this for stay detail pages so booking payloads that use imageUrl still show photos.
 */
export function getStayExperienceImageUrls(
  stay: Record<string, unknown>
): string[] {
  const images: string[] = [];

  if (stay.media && Array.isArray(stay.media)) {
    const mediaImages = stay.media
      .filter((item: unknown) =>
        isStayMediaImageRow(item as Record<string, unknown>)
      )
      .map((item: unknown) => (item as Record<string, unknown>).url as string);
    images.push(...mediaImages);
  }

  if (stay.imageUrl && Array.isArray(stay.imageUrl)) {
    for (const u of stay.imageUrl) {
      if (typeof u === 'string' && u) images.push(u);
    }
  }

  const uniqueImages = Array.from(new Set(images));
  if (uniqueImages.length === 0) {
    uniqueImages.push(getStayExperiencePrimaryImageUrl(stay));
  }

  return uniqueImages;
}

/**
 * Map Stay Experience to ExtendedExperienceCard
 * Used for related stay experiences on detail pages
 */
export function mapStayExperienceToCard(
  stay: Record<string, unknown>
): ExtendedExperienceCard {
  // Get location string
  const getLocation = (): string => {
    if (stay.location) {
      if (typeof stay.location === 'string') {
        return stay.location.trim() || 'Location TBD';
      }
      if (typeof stay.location === 'object') {
        const loc = stay.location as Record<string, unknown>;
        const locationStr =
          `${loc.city || loc.name || ''}${loc.state ? `, ${loc.state}` : ''}`.trim();
        return locationStr || 'Location TBD';
      }
    }
    return 'Location TBD';
  };

  // Get title
  const title =
    (typeof stay.shortTitle === 'string' ? stay.shortTitle.trim() : '') ||
    (typeof stay.name === 'string' ? stay.name.trim() : '') ||
    (typeof stay.title === 'string' ? stay.title.trim() : '') ||
    'Nexgo Space';

  // Get tags from categories
  const getTags = (): string[] => {
    const tags: string[] = [];

    console.log('[mapStayExperienceToCard] getTags - Raw data:', {
      category: stay.category,
      categories: stay.categories,
      categoryType: typeof stay.category,
      isCategoryArray: Array.isArray(stay.category),
      categoriesLength: Array.isArray(stay.categories)
        ? stay.categories.length
        : 0,
    });

    // Helper function to extract category name
    const extractCategoryName = (cat: unknown): string | null => {
      if (!cat) return null;
      if (typeof cat === 'string') {
        const trimmed = cat.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof cat === 'object' && cat !== null && !Array.isArray(cat)) {
        const catObj = cat as Record<string, unknown>;
        // Check multiple possible fields: title, name, description
        const title = catObj.title;
        const name = catObj.name;
        const description = catObj.description;
        const result =
          (typeof title === 'string' ? title.trim() : '') ||
          (typeof name === 'string' ? name.trim() : '') ||
          (typeof description === 'string' ? description.trim() : '');
        return result.length > 0 ? result : null;
      }
      return null;
    };

    // Add main category
    if (stay.category) {
      console.log(
        '[mapStayExperienceToCard] Processing category field:',
        stay.category
      );
      const catName = extractCategoryName(stay.category);
      console.log(
        '[mapStayExperienceToCard] Extracted from category:',
        catName
      );
      if (catName && !tags.includes(catName)) {
        tags.push(catName);
      }
    }

    // Add additional categories from categories array
    if (
      stay.categories &&
      Array.isArray(stay.categories) &&
      stay.categories.length > 0
    ) {
      console.log(
        '[mapStayExperienceToCard] Processing categories array:',
        stay.categories
      );
      stay.categories.forEach((cat: unknown, index: number) => {
        const catName = extractCategoryName(cat);
        console.log(
          `[mapStayExperienceToCard] Extracted from categories[${index}]:`,
          catName,
          'Raw:',
          cat
        );
        if (catName && !tags.includes(catName)) {
          tags.push(catName);
        } else if (catName) {
          console.log(
            `[mapStayExperienceToCard] Skipping duplicate category:`,
            catName
          );
        }
      });
    }

    console.log('[mapStayExperienceToCard] Final tags array:', tags);
    return tags;
  };

  // Get price from pricing object
  const getPrice = (): string | undefined => {
    if (stay.pricing) {
      const pricing = stay.pricing as Record<string, unknown>;
      // Check for group pricing
      if (
        pricing.groupPricing &&
        Array.isArray(pricing.groupPricing) &&
        pricing.groupPricing.length > 0
      ) {
        const displayGroup = pricing.groupPricing.find(
          (g: unknown) => (g as Record<string, unknown>).isDisplayPrice
        ) as Record<string, unknown> | undefined;
        if (displayGroup && displayGroup.adultPrice) {
          return (displayGroup.adultPrice as number).toFixed(2);
        }
      }

      // Fallback to pricePerPerson
      if (
        pricing.pricePerPerson !== null &&
        pricing.pricePerPerson !== undefined
      ) {
        return (pricing.pricePerPerson as number).toFixed(2);
      }
    }

    return undefined;
  };

  // Get nights from duration or default
  const nights =
    ((stay.duration as Record<string, unknown> | undefined)?.nights as
      | number
      | undefined) ||
    (stay.nights as number | undefined) ||
    1;

  // Get conversion rate from stay experience
  const getConversionRate = (): number | undefined => {
    // Check for targetCurrencyRate
    if (
      stay.targetCurrencyRate !== null &&
      stay.targetCurrencyRate !== undefined
    ) {
      const rate =
        typeof stay.targetCurrencyRate === 'number'
          ? stay.targetCurrencyRate
          : parseFloat(String(stay.targetCurrencyRate));
      if (!isNaN(rate) && rate > 0) return rate;
    }

    // Check for conversionRate
    if (stay.conversionRate !== null && stay.conversionRate !== undefined) {
      const rate =
        typeof stay.conversionRate === 'number'
          ? stay.conversionRate
          : parseFloat(String(stay.conversionRate));
      if (!isNaN(rate) && rate > 0) return rate;
    }

    // Check in pricing object
    if (stay.pricing) {
      const pricing = stay.pricing as Record<string, unknown>;
      if (
        pricing.targetCurrencyRate !== null &&
        pricing.targetCurrencyRate !== undefined
      ) {
        const rate =
          typeof pricing.targetCurrencyRate === 'number'
            ? pricing.targetCurrencyRate
            : parseFloat(String(pricing.targetCurrencyRate));
        if (!isNaN(rate) && rate > 0) return rate;
      }
    }

    return undefined;
  };

  return {
    id: (stay._id as string) || (stay.id as string) || '',
    title,
    image: getStayExperiencePrimaryImageUrl(stay),
    location: getLocation(),
    type: 'stays',
    slug: (stay.slug as string) || (stay._id as string),
    rating: (stay.averageRating as number) || 0,
    ratingCount: (stay.reviewCount as number) || 0,
    description:
      (stay.shortDescription as string) ||
      (stay.longDescription as string) ||
      (stay.description as string),
    price: getPrice(),
    currencySymbol:
      ((stay.pricing as Record<string, unknown> | undefined)
        ?.currencySymbol as string) ||
      (stay.currencySymbol as string) ||
      '₹',
    currency: (() => {
      // Prefer currency field from pricing object or root level
      if (stay.pricing) {
        const pricing = stay.pricing as Record<string, unknown>;
        if (pricing.currency && typeof pricing.currency === 'string') {
          return pricing.currency.toUpperCase();
        }
      }
      if (stay.currency && typeof stay.currency === 'string') {
        return stay.currency.toUpperCase();
      }
      // Fallback to currencySymbol inference
      const symbol =
        ((stay.pricing as Record<string, unknown> | undefined)
          ?.currencySymbol as string) ||
        (stay.currencySymbol as string) ||
        '₹';
      if (symbol === '₹' || symbol === 'INR') return 'INR';
      if (symbol === '$' || symbol === 'USD') return 'USD';
      return 'USD'; // Default
    })(),
    backendConversionRate: getConversionRate(),
    duration: undefined,
    attractionsCount: (stay.activities as unknown[] | undefined)?.length || 0,
    days: undefined,
    nights,
    locationsCount: stay.location ? 1 : 0,
    tags: getTags(),
    images: getStayExperienceImageUrls(stay),
  };
}

/**
 * Map array of stay experiences to ExtendedExperienceCard array
 */
export function mapStayExperiencesToCards(
  stays: Record<string, unknown>[]
): ExtendedExperienceCard[] {
  return stays.map(mapStayExperienceToCard);
}
